import getFooter from 'api/Footer/getFooter';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import { setHeadData } from 'utils/metaDataUtils/headData';
import type { HeaderNav } from 'interfaces/navigation/Header';
import getFranchiseListItem from 'api/FranchiseChildPage/getFranchiseListItem';
import type { FranchiseListItem } from 'interfaces/content/Ranking';
import PeopleRankingPage from 'pages/PeopleRanking/PeopleRankingPage';
import { setPianoConfig } from 'services/Piano';
import setFranchisePageAdConfig from 'services/Ad/config/setFranchisePageAdConfig';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import type { PreviewData } from 'interfaces/PreviewData';
import { Microsites } from 'constants/constants';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { PianoPaywallPageType } from '@/app_lib/types/piano/Paywall';

interface PersonListItemProps {
  franchiseListItem: FranchiseListItem;
}

/**
 * Franchise People List Item Child Page
 * ranking/40-under-40/2020/john-doe
 */
const PersonListItem = ({ franchiseListItem }: PersonListItemProps) => (
  <PeopleRankingPage
    franchiseItem={franchiseListItem}
    trackingData={{
      eventAction: '',
      eventCategory: 'rankings',
      platformCD: 'own',
    }}
  />
);

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { franchiseSlug, year, personSlug } = context.params as {
      franchiseSlug: string;
      year: string;
      personSlug: string;
    };

    const { previewData } = context as { previewData: PreviewData };

    const franchisePersonSlug = `/${franchiseSlug}/${year}/${personSlug}`;
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const { franchiseListItem } = await getFranchiseListItem(franchisePersonSlug, previewData, Microsites.Own);
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(franchiseListItem), fallbackData.dataLayer);
    const headData = safe(() => setHeadData(franchiseListItem), fallbackData.headData);
    const arcSpanPageConfig = safe(() => setarcSpanPageConfig(franchiseListItem), fallbackData.arcSpanPageConfig);
    const pianoPageConfig = safe(
      () => setPianoConfig({ pageData: franchiseListItem, pageType: PianoPaywallPageType.People }),
      fallbackData.pianoPageConfig,
    );
    const pageAdConfig = safe(() => setFranchisePageAdConfig(franchiseListItem), fallbackData.pageAdConfig);

    pageAdConfig.ch = personSlug.replace(/-/g, '');

    if (franchiseSlug === 'modern-board-25') pageAdConfig.topics = ['themodernboard'];

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        franchiseListItem,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            franchiseSlug: '40-under-40',
            personSlug: 'benji-backer',
            year: '2021',
          },
        },
      ],
    };
  }

  return {
    fallback: 'blocking',
    paths: [],
  };
};

export default PersonListItem;
